<script>
  // Публикация чата на сайт (этап 26.4): содержимое шторки из шапки чата и карточки
  // «Публикация» на экране контекста. Не опубликован — дерево будущего раздела,
  // флажки и «Опубликовать»; опубликован — состояние, ссылки, флажки, выбор версии
  // при правке с двух сторон, последние изменения и «Снять с публикации».
  // Этап 26.5 — статья о проекте: «Собрать статью» → черновик пишется фоном → просмотр
  // целиком → «Опубликовать статью»; на сайте — флажок, «Пересобрать», пометка «устарела».
  // articleTick — родитель увеличивает его по событию publication с полем article.
  // События: changed({ publication }), close-stage-and-publish({ includeTranscript, autoStages }).
  import { createEventDispatcher } from 'svelte';
  import { api } from '../api.js';
  import { renderMarkdown } from '../markdown.js';
  import { createArm } from '../arm.js';
  import { TEXT } from '../ui-text.js';
  import {
    conflictFiles,
    formatTime,
    materialPreview,
    pageLabel,
    previewTree,
    readyStageCount,
    statusLine,
  } from '../publication.js';

  export let chatId;
  export let publication = null;
  export let stages = [];
  export let hasState = false;
  export let articleTick = 0;

  const dispatch = createEventDispatcher();

  let includeTranscript = true;
  let autoStages = true;
  let busy = false;
  let error = '';
  // В13 (1.27.8): «Снять с публикации» и «Отказаться» от статьи — вторым касанием,
  // взведённая кнопка сама остывает (раньше «Снять» оставалась взведённой навсегда).
  const { armed: pubArmed, tap: tapPub } = createArm();
  let comparePath = null;
  let article = null;
  let articlePreview = false;

  $: published = Boolean(publication?.published);
  $: if (published) {
    includeTranscript = publication.includeTranscript;
    autoStages = publication.autoStages;
  }
  $: tree = previewTree(stages, { hasState, includeTranscript });
  $: noStages = readyStageCount(stages) === 0;
  $: conflicts = conflictFiles(publication);
  // Статья: грузим при публикации и по событию (articleTick).
  $: articleKey = published && chatId ? `${chatId}:${articleTick}` : null;
  $: if (articleKey) {
    loadArticle();
  }
  $: previewing = articlePreview && article?.status === 'ready' && Boolean(article?.draft);

  async function loadArticle() {
    try {
      article = await api.getArticle(chatId);
    } catch {
      article = null;
    }
  }

  async function articleAction(action) {
    busy = true;
    error = '';
    try {
      article = await action();
    } catch (e) {
      error = e?.message || TEXT.publish.failed;
    } finally {
      busy = false;
    }
  }

  function generateArticle() {
    articlePreview = false;
    return articleAction(() => api.generateArticle(chatId));
  }

  async function acceptArticle() {
    await articleAction(() => api.acceptArticle(chatId));
    articlePreview = false;
    const next = await api.getPublication(chatId).catch(() => null);
    if (next) {
      dispatch('changed', { publication: next });
    }
  }

  function discardArticle() {
    if (!tapPub('discard')) {
      return undefined;
    }
    articlePreview = false;
    return articleAction(() => api.discardArticleDraft(chatId));
  }

  async function toggleArticle(value) {
    await toggle('includeArticle', value);
    await loadArticle();
  }

  // Конфликт уже разрешён (файла нет в списке) — шаблон сам вернётся к обычному виду.
  $: compared = comparePath ? conflicts.find((file) => file.path === comparePath) || null : null;

  async function run(action, fallback = TEXT.publish.failed) {
    busy = true;
    error = '';
    try {
      const next = await action();
      if (next) {
        dispatch('changed', { publication: next });
      }
      return next;
    } catch (e) {
      error = e?.message || fallback;
      return null;
    } finally {
      busy = false;
    }
  }

  function publish() {
    return run(() => api.publishChat(chatId, { includeTranscript, autoStages }));
  }

  function toggle(field, value) {
    return run(() => api.updatePublication(chatId, { [field]: value }));
  }

  function resolve(keep) {
    const path = comparePath;
    return run(async () => {
      const next = await api.resolvePublication(chatId, path, keep);
      comparePath = null;
      return next;
    });
  }

  async function unpublish() {
    if (!tapPub('unpublish')) {
      return;
    }
    await run(async () => {
      await api.unpublishChat(chatId);
      return { available: true, published: false, files: [], commits: [] };
    });
  }
</script>

<div class="publication" data-testid="publication-panel">
  {#if error}
    <p class="pub-error" role="alert">{error}</p>
  {/if}

  {#if !published}
    <p class="pub-intro">{TEXT.publish.intro}</p>
    <div class="pub-block">
      <div class="pub-label">{TEXT.publish.treeTitle}</div>
      <ul class="pub-tree" data-testid="publication-tree">
        {#each tree as item, index (index)}
          <li data-kind={item.kind}>{item.label}</li>
        {/each}
      </ul>
      {#if noStages}
        <p class="pub-hint">{TEXT.publish.noStages}</p>
      {/if}
    </div>
    <label class="pub-check">
      <input type="checkbox" bind:checked={includeTranscript} disabled={busy} />
      <span>{TEXT.publish.includeTranscript}<small>{TEXT.publish.includeTranscriptHint}</small></span>
    </label>
    <label class="pub-check">
      <input type="checkbox" bind:checked={autoStages} disabled={busy} />
      <span>{TEXT.publish.autoStages}<small>{TEXT.publish.autoStagesHint}</small></span>
    </label>
    <div class="pub-actions">
      <button type="button" class="pub-primary" on:click={publish} disabled={busy}>
        {busy ? TEXT.publish.publishing : TEXT.publish.publish}
      </button>
      {#if noStages}
        <button
          type="button"
          class="pub-secondary"
          disabled={busy}
          on:click={() => dispatch('close-stage-and-publish', { includeTranscript, autoStages })}
        >{TEXT.publish.closeAndPublish}</button>
      {/if}
    </div>
  {:else if previewing}
    <p class="pub-hint">{TEXT.publish.article.previewHint}</p>
    <div class="pub-article markdown-body" data-testid="article-preview">{@html renderMarkdown(materialPreview(article.draft))}</div>
    <div class="pub-actions">
      <button type="button" class="pub-primary" disabled={busy} on:click={acceptArticle}>{TEXT.publish.article.accept}</button>
      <button type="button" class="pub-secondary" disabled={busy} on:click={generateArticle}>{TEXT.publish.article.regenerate}</button>
      <button type="button" class="pub-link" class:pub-armed={$pubArmed === 'discard'} disabled={busy} on:click={discardArticle}>
        {$pubArmed === 'discard' ? TEXT.publish.article.discardConfirm : TEXT.publish.article.discard}
      </button>
      <button type="button" class="pub-link" disabled={busy} on:click={() => (articlePreview = false)}>{TEXT.publish.back}</button>
    </div>
  {:else if compared}
    <p class="pub-hint">{compared.state === 'unparsed' ? TEXT.publish.unparsedHint : TEXT.publish.conflictHint}</p>
    <div class="pub-version" data-testid="publication-version-git">
      <div class="pub-label">{TEXT.publish.versionGit} · {pageLabel(compared, stages)}</div>
      {#if compared.state === 'unparsed'}
        <pre class="pub-raw">{compared.conflictText || ''}</pre>
      {:else}
        <div class="markdown-body">{@html renderMarkdown(compared.conflictText || '')}</div>
      {/if}
    </div>
    <div class="pub-version" data-testid="publication-version-petrovich">
      <div class="pub-label">{TEXT.publish.versionPetrovich}</div>
      <div class="markdown-body">{@html renderMarkdown(compared.currentText || '')}</div>
    </div>
    <div class="pub-actions">
      {#if compared.state !== 'unparsed'}
        <button type="button" class="pub-primary" disabled={busy} on:click={() => resolve('git')}>{TEXT.publish.keepGit}</button>
      {/if}
      <button type="button" class="pub-secondary" disabled={busy} on:click={() => resolve('petrovich')}>{TEXT.publish.keepPetrovich}</button>
      <button type="button" class="pub-link" disabled={busy} on:click={() => (comparePath = null)}>{TEXT.publish.back}</button>
    </div>
  {:else}
    <p class="pub-status" data-status={publication.status} role="status">{statusLine(publication)}</p>
    <div class="pub-links">
      <a href={publication.url} target="_blank" rel="noopener noreferrer" data-testid="publication-site-link">{TEXT.publish.openSite}</a>
      <a href={publication.repoUrl} target="_blank" rel="noopener noreferrer">{TEXT.publish.openGit}</a>
    </div>

    {#if article}
      <div class="pub-block" data-testid="publication-article">
        <div class="pub-label">{TEXT.publish.article.title}</div>
        {#if article.status === 'pending'}
          <p class="pub-hint" role="status"><span class="spinner small" aria-hidden="true"></span> {TEXT.publish.article.pending}</p>
        {:else if article.status === 'ready' && article.draft}
          <p class="pub-hint">{TEXT.publish.article.draftReady}</p>
        {:else if article.status === 'failed'}
          <p class="pub-error" role="alert">{TEXT.publish.article.failed.replace('{error}', article.error || '')}</p>
        {:else if !article.text}
          <p class="pub-hint">{article.canGenerate ? TEXT.publish.article.hint : TEXT.publish.article.noSources}</p>
        {/if}
        {#if article.published}
          <p class="pub-hint">
            {TEXT.publish.article.published} · <a href={article.url} target="_blank" rel="noopener noreferrer" data-testid="article-site-link">{TEXT.publish.article.open}</a>
            {#if article.editedAt}<small class="pub-git">{TEXT.publish.importedMark.replace('{time}', formatTime(article.editedAt))}</small>{/if}
          </p>
          {#if article.stale}
            <p class="pub-hint pub-stale">{TEXT.publish.article.stale}</p>
          {/if}
        {/if}
        {#if article.text}
          <label class="pub-check">
            <input type="checkbox" checked={publication.includeArticle} disabled={busy}
                   on:change={(e) => toggleArticle(e.currentTarget.checked)} />
            <span>{TEXT.publish.article.onSite}</span>
          </label>
        {/if}
        <div class="pub-actions">
          {#if article.status === 'ready' && article.draft}
            <button type="button" class="pub-primary" disabled={busy} on:click={() => (articlePreview = true)}>{TEXT.publish.article.preview}</button>
          {/if}
          {#if article.status !== 'pending' && (article.canGenerate || article.text)}
            <button type="button" class="pub-secondary" disabled={busy || !article.canGenerate} on:click={generateArticle}>
              {article.status === 'failed' ? TEXT.publish.article.retry : article.text || article.draft ? TEXT.publish.article.regenerate : TEXT.publish.article.generate}
            </button>
          {/if}
        </div>
      </div>
    {/if}

    {#if conflicts.length}
      <div class="pub-block" data-testid="publication-conflicts">
        <div class="pub-label">{TEXT.publish.conflictsTitle}</div>
        {#each conflicts as file (file.path)}
          <div class="pub-conflict">
            <span>{pageLabel(file, stages)}</span>
            <button type="button" class="pub-secondary small" on:click={() => (comparePath = file.path)}>{TEXT.publish.compare}</button>
          </div>
        {/each}
      </div>
    {/if}

    <label class="pub-check">
      <input type="checkbox" checked={includeTranscript} disabled={busy}
             on:change={(e) => toggle('includeTranscript', e.currentTarget.checked)} />
      <span>{TEXT.publish.includeTranscript}<small>{TEXT.publish.includeTranscriptHint}</small></span>
    </label>
    <label class="pub-check">
      <input type="checkbox" checked={autoStages} disabled={busy}
             on:change={(e) => toggle('autoStages', e.currentTarget.checked)} />
      <span>{TEXT.publish.autoStages}<small>{TEXT.publish.autoStagesHint}</small></span>
    </label>

    {#if publication.commits?.length}
      <div class="pub-block">
        <div class="pub-label">{TEXT.publish.commitsTitle}</div>
        <ul class="pub-commits">
          {#each publication.commits as commit (commit.sha)}
            <li><span>{commit.message}</span><small>{commit.author} · {formatTime(commit.date)}</small></li>
          {/each}
        </ul>
      </div>
    {/if}

    <div class="pub-actions">
      <button type="button" class="pub-secondary" disabled={busy} on:click={publish}>{TEXT.publish.syncNow}</button>
      <button type="button" class="pub-danger" disabled={busy} on:click={unpublish}>
        {$pubArmed === 'unpublish' ? TEXT.publish.unpublishConfirm : TEXT.publish.unpublish}
      </button>
    </div>
  {/if}
</div>

<style>
  .pub-armed {
    color: var(--error-text);
    font-weight: 600;
  }

  .publication {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 0.25rem 0.5rem 0.5rem;
    -webkit-user-select: text;
    user-select: text;
  }

  .pub-intro,
  .pub-hint,
  .pub-status {
    font-size: 0.875rem;
    color: var(--text-secondary);
    margin: 0;
  }

  .pub-status[data-status='conflict'],
  .pub-status[data-status='error'],
  .pub-error {
    color: var(--error-text);
  }

  .pub-error {
    margin: 0;
    font-size: 0.875rem;
  }

  .pub-label {
    font-size: 0.75rem;
    color: var(--text-muted);
    margin-bottom: 0.3rem;
  }

  .pub-tree,
  .pub-commits {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    font-size: 0.9375rem;
  }

  .pub-tree li {
    padding-left: 1rem;
    position: relative;
  }

  .pub-tree li::before {
    content: '';
    position: absolute;
    left: 0.2rem;
    top: 0.55em;
    width: 0.35rem;
    height: 0.35rem;
    border-radius: 50%;
    background: var(--text-muted);
  }

  .pub-commits li {
    display: flex;
    flex-direction: column;
  }

  .pub-commits small,
  .pub-check small {
    display: block;
    font-size: 0.75rem;
    color: var(--text-muted);
  }

  .pub-check {
    display: flex;
    gap: 0.6rem;
    align-items: flex-start;
    font-size: 0.9375rem;
  }

  .pub-check input {
    margin-top: 0.2rem;
  }

  .pub-links {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .pub-links a {
    color: var(--accent-color);
    font-size: 0.9375rem;
  }

  .pub-conflict {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    font-size: 0.9375rem;
  }

  .pub-version {
    border: 1px solid var(--border-light);
    border-radius: 10px;
    padding: 0.5rem 0.75rem;
    max-height: 30vh;
    overflow: auto;
  }

  .pub-article {
    border: 1px solid var(--border-light);
    border-radius: 10px;
    padding: 0.5rem 0.75rem;
    max-height: 55vh;
    overflow: auto;
  }

  .pub-git {
    display: block;
    color: var(--accent-color);
    font-size: 0.75rem;
  }

  .pub-stale {
    color: var(--warning-text, var(--text-secondary));
  }

  .pub-raw {
    white-space: pre-wrap;
    font-size: 0.8125rem;
    margin: 0;
  }

  .pub-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .pub-primary,
  .pub-secondary,
  .pub-danger {
    min-height: 40px;
    padding: 0 0.9rem;
    border-radius: 10px;
    border: none;
    font-size: 0.9375rem;
  }

  .pub-primary {
    background: var(--accent-color);
    color: #fff;
  }

  .pub-secondary {
    background: var(--bg-tertiary);
    color: var(--text-primary);
  }

  .pub-secondary.small {
    min-height: 32px;
    font-size: 0.8125rem;
  }

  .pub-danger {
    background: none;
    color: var(--error-text);
  }

  .pub-link {
    background: none;
    border: none;
    color: var(--text-secondary);
  }

  button:disabled {
    opacity: 0.5;
  }
</style>
